import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import InfoCard from "../../../components/components/js/InfoCard";
import { FaUserCircle } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import AuthContext from "../../../context/AuthProvider";
import { DeleteConfirmationDialog } from "../../../components/components/js/DeleteConfirmationDialog";
import { Avatar, Typography, Box, Button, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { ADMIN_ADD_SCHOOL_ROUTE, ADMIN_SCHOOL_EDIT_ROUTE } from "../../../routes/RoutesPathes";
import SchoolStorage from "../../../utils/localStorage";
// import { toast } from "react-toastify";

export const SchoolEdit = () => {
  const { staffId } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [cardData, setCardData] = useState([]);
  const { school, setSchool, schools, setSchools } = useContext(AuthContext);
  const fileInputRef = useRef();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [schoolData, setSchoolData] = useState({});
  // Card label -> V2 nursery field
  const schoolFields = {
    Name: "name",
    Description: "description",
    Capacity: "capacity",
    Phone: "phone",
    Country: "country",
    "Program Type": "program_type",
  };

  const applySchoolUpdate = (currentCards, updated) =>
    currentCards.map((card) => {
      if (!card.fullWidth) return card;
      return {
        ...card,
        data: card.data.map((item) => {
          const key = schoolFields[item.label];
          if (key && updated && Object.prototype.hasOwnProperty.call(updated, key)) {
            return { ...item, value: updated[key] };
          }
          return item;
        }),
      };
    });

  // Keep the user menu / schools dropdown in sync with the current nursery
  const syncCurrentSchool = (updatedNursery) => {
    const name = updatedNursery?.name ?? school.name;
    const logo = updatedNursery?.logo ?? school.logo;
    setSchool((prev) => ({ ...prev, name, logo }));
    SchoolStorage.setSchool(school.id, name, logo);

    const nextSchools = (schools || []).map((existingSchool) =>
      String(existingSchool.value) === String(school.id)
        ? { ...existingSchool, label: name, logo }
        : existingSchool
    );
    setSchools(nextSchools);
    window.dispatchEvent(
      new CustomEvent("schools:updated", { detail: { schools: nextSchools } })
    );
  };

  const fetchSchool = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);

      const [schoolRes, programRes, countriesRes] = await Promise.all([
        axiosPrivate.get(API_URLS.getSchool(school.id)),
        axiosPrivate.get(API_URLS.programType),
        axiosPrivate.get(API_URLS.country),
      ]);
      const countryRows = Array.isArray(countriesRes.data)
        ? countriesRes.data
        : countriesRes.data?.results || [];
      const programRows = Array.isArray(programRes.data)
        ? programRes.data
        : programRes.data?.results || [];

      const countries = countryRows.map((country) => ({
        id: country.code || country.Code,
        name: country.name || country.Name,
      }));
      const programTypes = programRows.map((program) => ({
        id: program.id,
        name: program.name || program.Name,
      }));

      const nursery = schoolRes.data;
      setSchoolData(nursery);
      const card = [
        {
          label: "Name",
          value: nursery.name,
          editable: true,
        },
        {
          label: "Description",
          value: nursery.description,
          editable: true,
        },
        { label: "Capacity", value: nursery.capacity, editable: true },
        { label: "Phone", value: nursery.phone, editable: true },
        {
          label: "Country",
          value: nursery.country,
          type: "select",
          options: countries,
          editable: true,
        },
        {
          label: "Program Type",
          value: nursery.program_type,
          type: "select",
          options: programTypes,
          editable: true,
        },
        // { label: "main_school", value: nursery.main_school, editable: false },
      ];
      const finalData = [
        { type: "info", title: "School Data", data: card, fullWidth: true },
      ];
      setCardData(finalData);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        console.error("Error fetching data", err);
        setError("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, school.id]);

  useEffect(() => {
    fetchSchool();
  }, [fetchSchool]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      await axiosPrivate.delete(API_URLS.getSchool(school.id));

      const remainingSchools = (schools || []).filter(
        (existingSchool) => String(existingSchool.value) !== String(school.id)
      );

      setSchools(remainingSchools);
      window.dispatchEvent(
        new CustomEvent("schools:updated", { detail: { schools: remainingSchools } })
      );

      if (remainingSchools.length > 0) {
        const nextSchool = remainingSchools[0];
        setSchool({ id: nextSchool.value, name: nextSchool.label, logo: nextSchool.logo });
        SchoolStorage.setSchool(nextSchool.value, nextSchool.label, nextSchool.logo);
        navigate(ADMIN_SCHOOL_EDIT_ROUTE);
      } else {
        SchoolStorage.clearSchool();
        navigate(ADMIN_ADD_SCHOOL_ROUTE);
      }
    } catch (error) {
      console.error("Delete request error:", error);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const updateImage = async (img) => {
    try {
      setLoading(true);
      setError(null);
      const formData = new FormData();
      formData.append("logo", img);
      const response = await axiosPrivate.patch(
        API_URLS.getSchool(school.id),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const newLogoUrl = response?.data?.logo;

      if (newLogoUrl) {
        // Update local state, context (UserMenu/Header) and localStorage
        setSchoolData((prev) => ({ ...prev, logo: newLogoUrl }));
        syncCurrentSchool(response.data);
      }
    } catch (err) {
      console.error("Patch error", err);
      setError("Failed to update. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      updateImage(file);
    }
  };

  const handleEdit = async (diff) => {
    try {
      setLoading(true);
      setError(null);
      const url =  API_URLS.getSchool(school.id);
      const response = await axiosPrivate.patch(url, diff, {
        headers: { "Content-Type": "application/json" },
      });
      const updatedNursery = { ...schoolData, ...diff, ...(response?.data || {}) };

      setSchoolData(updatedNursery);
      setCardData((prev) => applySchoolUpdate(prev, updatedNursery));
      if (Object.prototype.hasOwnProperty.call(diff, "name")) {
        syncCurrentSchool(updatedNursery);
      }
    } catch (err) {
      console.error("Patch error", err);
      setError("Failed to update. Please try again.");
      // Revert the card to the saved values
      fetchSchool();
    } finally {
      setLoading(false);
    }
  };

  const fullWidthCards = cardData.filter((card) => card.fullWidth);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="80vh"
        textAlign="center"
      >
        <Typography variant="h6" color="textSecondary">
          {t("buttons.loading")}
        </Typography>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="80vh"
        textAlign="center"
      >
        <Typography variant="h4" color="primary" gutterBottom>
          {t(`SchoolEdit.notFound.title`)}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t(`SchoolEdit.notFound.description`)}
        </Typography>
      </Box>
    );
  }

  return (
    <div
      className={`p-5 d-block ms-auto px-4`}
      style={{
        minHeight: "70vh",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: "5%", pr: "35px" }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box position="relative" display="inline-block">
            <Box
              sx={(theme) => ({
                position: "absolute",
                top: -4,
                left: -4,
                width: 93,
                height: 93,
                borderRadius: "50%",
                border: `1px solid ${theme.palette.primary.main}`,
                pointerEvents: "none",
              })}
            />
            {schoolData.logo ? (
              <Avatar
                src={schoolData.logo}
                alt="img"
                sx={{ width: 85, height: 85 }}
              />
            ) : (
              <FaUserCircle size={85} />
            )}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <IconButton
              size="small"
              sx={(theme) => ({
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 25,
                height: 25,
                backgroundColor: "white",
                borderRadius: "50%",
                boxShadow: 1,
                border: `1px solid ${theme.palette.primary.main}`,
                "&:hover": { backgroundColor: "#e3f2fd" },
              })}
              aria-label="edit"
            >
              <EditIcon
                fontSize="small"
                color="primary"
                onClick={handleEditClick}
              />
            </IconButton>
          </Box>

          <Typography variant="h5" fontWeight="bold">
            {schoolData?.name}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            onClick={() => navigate(ADMIN_ADD_SCHOOL_ROUTE)}
            sx={{
              borderRadius: "50px",
              fontSize: "0.9rem",
              px: 3,
              textTransform: "none",
            }}
          >
            Add New School
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={loading}
            sx={{
              borderRadius: "50px",
              fontSize: "0.9rem",
              px: 3,
              textTransform: "none",
            }}
          >
            {t("SchoolEdit.deleteButton")}
          </Button>
        </Box>
      </Box>

      <div style={{ padding: "35px" }}>
        {fullWidthCards.map((data, idx) => (
          <div key={`full-${idx}`}>
            <InfoCard
              title={data.title}
              editable
              data={data.data}
              id={staffId}
              handleEdit={handleEdit}
              type={"school"}
            />
          </div>
        ))}
        {showDeleteConfirm && (
          <DeleteConfirmationDialog
            onDelete={handleDelete}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
      </div>
    </div>
  );
};
